import React, { useState } from "react";
import { fmtDatetime } from "utils/time-utils";
import { TabPanel, TabView, TabViewTabChangeEvent } from "primereact/tabview";
import { T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { TimeOptionKeys, TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { useTimezone } from "hooks/useTimezone";
import { DATE_FORMATTER_MONTH_DAY_ONLY } from "utils/constants";
import { WeatherMonitoringProps } from "../charts/interfaces";
import { getStartEndTime, TabTypes } from "./WeatherMonitoringUtils";
import WeatherMonitoringTable from "./WeatherMonitoringTable";
import ChartPanel from "./ChartPanel";

export default function WeatherMonitoring(props: WeatherMonitoringProps) {
  const tz = useTimezone();

  const [timeInterval, setTimeInterval] = useState<TimeOptionKeys>(props.timeInterval as TimeOptionKeys);
  const [startTime, setStartTime] = useState<Date>(
    getStartEndTime(props.timeInterval as TimeOptionKeys, "start")
  );
  const [endTime, setEndTime] = useState<Date>(getStartEndTime(props.timeInterval as TimeOptionKeys, "end"));
  const [activeIndex, setActiveIndex] = useState<number>(
    props.timeInterval === TimeOptions.HOURS.name ? 0 : 1
  );
  const [activeIndexTable, setActiveIndexTable] = useState<number>(0);

  const isChartVisible = (): boolean | undefined => {
    return (
      props.temperature?.isVisible &&
      props.humidity?.isVisible &&
      props.precipitation?.isVisible &&
      props.windSpeed?.isVisible &&
      props.windDirection?.isVisible &&
      !!props.temperature.dataset?.identifier &&
      !!props.humidity.dataset?.identifier &&
      !!props.precipitation.dataset?.identifier &&
      !!props.windSpeed.dataset?.identifier &&
      !!props.windDirection.dataset?.identifier
    );
  };

  const datasetsIdentifiers = [
    props.temperature?.dataset?.identifier,
    props.feelsLikeTemperature?.dataset?.identifier,
    props.windSpeed?.dataset?.identifier,
    props.windDirection?.dataset?.identifier,
    props.humidity?.dataset?.identifier,
    props.precipitation?.dataset?.identifier,
    props.gusture?.dataset?.identifier,
  ].filter((identifier): identifier is string => !!identifier);

  const { data: datasetsData, isLoading } = useGetDataInfoRange(
    datasetsIdentifiers,
    {
      timeStart: startTime.toISOString(),
      timeEnd: endTime.toISOString(),
    },
    {
      enabled: isChartVisible(),
    }
  );

  const handleTabChange = (e: TabViewTabChangeEvent, tabsType: string) => {
    if (tabsType === TabTypes.TABS) {
      setActiveIndex(e.index);
      const currentTimeInterval = e.index === 0 ? "HOURS" : "DAYS";
      setTimeInterval(currentTimeInterval as TimeOptionKeys);
      setStartTime(getStartEndTime(currentTimeInterval as TimeOptionKeys, "start"));
      setEndTime(getStartEndTime(currentTimeInterval as TimeOptionKeys, "end"));
    }
    if (tabsType === TabTypes.SUBTABS) {
      setActiveIndexTable(e.index);
    }
  };

  const handleFormatHeaderString = (dayOffset: number) => {
    return fmtDatetime(
      new Date(new Date().setDate(new Date().getDate() + dayOffset)),
      tz.display,
      DATE_FORMATTER_MONTH_DAY_ONLY
    );
  };

  return (
    <>
      <div className="flex flex-column gap-2">
        <div className="font-semibold text-xl">{props.location}</div>
        {props.dateSelection?.isVisible && (
          <div className="flex flex-row gap-2">
            <CalendarWrapper
              id="from"
              value={startTime}
              onChange={setStartTime}
              className="p-mr-2"
              debounceTimeInMs={300}
            />
          </div>
        )}
      </div>
      {!isChartVisible() ? (
        <div className="text-center">
          {T("message.weather-monitoring.to-display-chart-please-select-these-datasets")}
        </div>
      ) : (
        <TabView activeIndex={activeIndex} onTabChange={e => handleTabChange(e, TabTypes.TABS)}>
          <TabPanel header={T("word.weather-monitoring.graphs-hourly")}>
            {datasetsData && (
              <ChartPanel
                datasetsData={datasetsData}
                currentChartData={props}
                timeInterval={TimeOptions.HOURS.name as TimeOptionKeys}
                startTime={startTime}
                endTime={endTime}
                isChartVisible={!!isChartVisible()}
                reportMode={props.reportMode ?? false}
              />
            )}
          </TabPanel>
          <TabPanel header={T("word.weather-monitoring.graphs-daily")}>
            {datasetsData && (
              <ChartPanel
                datasetsData={datasetsData}
                currentChartData={props}
                timeInterval={TimeOptions.DAYS.name as TimeOptionKeys}
                startTime={startTime}
                endTime={endTime}
                isChartVisible={!!isChartVisible()}
                reportMode={props.reportMode ?? false}
              />
            )}
          </TabPanel>
          <TabPanel header={T("word.table")}>
            <TabView activeIndex={activeIndexTable} onTabChange={e => handleTabChange(e, TabTypes.SUBTABS)}>
              {[0, 1, 2].map(dayOffset => (
                <TabPanel key={dayOffset} header={handleFormatHeaderString(dayOffset)}>
                  {timeInterval === "DAYS" && !isLoading && datasetsData && (
                    <WeatherMonitoringTable
                      datasetsData={datasetsData}
                      props={props}
                      dayOffset={dayOffset}
                      endHour={dayOffset === 2 ? new Date().getHours() : 23}
                      tabHeader={handleFormatHeaderString(dayOffset)}
                    />
                  )}
                </TabPanel>
              ))}
            </TabView>
          </TabPanel>
        </TabView>
      )}
    </>
  );
}
